import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import SiteFooter from '../../components/feature/SiteFooter';

const values = [
  { icon: 'ri-shield-check-line', title: 'Radical Honesty', desc: 'We write what we actually think — pros and cons, not paid promotion. Every score is earned through testing.', color: 'from-violet-500 to-fuchsia-500' },
  { icon: 'ri-microscope-line', title: 'Hands-On Testing', desc: 'Every tool we review is actually used by our team. No second-hand opinions, no copy-pasting spec sheets.', color: 'from-emerald-500 to-teal-500' },
  { icon: 'ri-refresh-line', title: 'Always Up-to-Date', desc: 'Platforms change constantly. We monitor updates and refresh our reviews so you\'re never working with stale info.', color: 'from-orange-400 to-pink-500' },
  { icon: 'ri-bar-chart-grouped-line', title: 'Data-Driven Scores', desc: 'Ratings use a weighted rubric across 7+ criteria — not gut feelings. Transparent, reproducible methodology.', color: 'from-sky-500 to-cyan-500' },
];

export default function AboutPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: 'About 247digitrend – Honest Digital Tool Reviews',
        url: `${siteUrl}/about`,
        description:
          '247digitrend is an independent review platform operated by 247 Digital Marketing Co., Ltd. We test every tool hands-on and publish unbiased scores across 7+ criteria.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
          ],
        },
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: '247digitrend',
        legalName: '247 Digital Marketing Co., Ltd.',
        url: siteUrl,
        foundingDate: '2021',
        numberOfEmployees: { '@type': 'QuantitativeValue', value: '6' },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Ho Chi Minh City',
          addressCountry: 'VN',
        },
        contactPoint: [
          { '@type': 'ContactPoint', contactType: 'customer support', email: 'hello@247digitrend.com' },
          { '@type': 'ContactPoint', contactType: 'editorial', email: 'editorial@247digitrend.com' },
        ],
      },
    ],
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="About 247digitrend – Honest Unbiased Digital Tool Reviews Since 2021"
        description="Learn about 247digitrend – an independent review platform that hands-on tests 500+ digital tools with transparent scoring. Founded in 2021 by a team of builders and entrepreneurs."
        canonical={`${siteUrl}/about`}
        keywords="about 247digitrend, digital tool review platform, unbiased software reviews, 247 digital marketing"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-44 pb-20" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-information-line"></i> About 247digitrend
          </span>
          <h1 className="text-5xl font-black text-gray-900 mb-5 leading-tight">
            Honest Reviews.<br />
            <span style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Real Decisions.
            </span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            We&apos;re a team of writers, engineers, and entrepreneurs who got tired of biased reviews and paid rankings. So we built the resource we always wanted.
          </p>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-3 block">Our Story</span>
              <h2 className="text-3xl font-black text-gray-900 mb-6 leading-tight">We started because we were frustrated</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                In 2021, our founders — a group of small business owners and freelancers — were drowning in conflicting advice about which tools to use. Every "review" site seemed to push the tools with the highest affiliate commissions, not the best products.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                247digitrend was born from a simple promise: test every tool we cover, score it on transparent criteria, and say what we actually think — even if the tool pays us nothing.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Today we&apos;ve reviewed 500+ tools across 20+ categories, reaching 50,000 monthly readers who rely on us to make confident purchasing decisions.
              </p>
              <div className="grid grid-cols-3 gap-6">
                {[{ num: '500+', label: 'Tools Reviewed' }, { num: '50K+', label: 'Monthly Readers' }, { num: '3+', label: 'Years Running' }].map((s, i) => (
                  <div key={i} className="text-center p-4 rounded-2xl border border-violet-100 bg-violet-50/50">
                    <div className="text-2xl font-black mb-1" style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.num}</div>
                    <div className="text-xs text-gray-500 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/09abe0d50a152cab841b9dee9c00b51d.jpg" alt="Our team at work" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 border border-gray-100" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500">
                    <i className="ri-shield-check-line text-white text-base"></i>
                  </div>
                  <div>
                    <div className="text-sm font-black text-gray-900">100% Independent</div>
                    <div className="text-xs text-gray-500">No paid placements</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20" style={{ background: '#faf5ff' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-3 block">What We Stand For</span>
            <h2 className="text-3xl font-black text-gray-900 mb-3">Our Core Values</h2>
            <p className="text-sm text-gray-500">These aren&apos;t just words on a wall — they shape every review we publish</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${v.color} mb-4`}>
                  <i className={`${v.icon} text-white text-lg`}></i>
                </div>
                <h3 className="text-sm font-black text-gray-900 mb-2">{v.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #c026d3 60%, #ec4899 100%)' }}>
        <div className="max-w-2xl mx-auto px-6 text-center text-white">
          <h2 className="text-2xl font-black mb-3">Have a tool you want us to review?</h2>
          <p className="text-white/80 text-sm mb-6">Submit your product and our team will evaluate it against our full criteria.</p>
          <div className="flex items-center justify-center gap-4">
            <a href="/submit-tool" className="bg-white text-violet-700 font-bold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-violet-50 transition-colors">
              Submit a Tool
            </a>
            <a href="/contact" className="border border-white/40 text-white font-semibold text-sm px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-white/10 transition-colors">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />
    </div>
  );
}
